import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

// All conversions go through pixels (CSS reference unit @ 96 DPI).
// Static factors first; em/rem depend on user-provided font sizes.
const STATIC_TO_PX = {
  px: 1,
  pt: 96 / 72, // 1pt = 1/72 in
  pc: 16, // 1pc = 12pt
  in: 96,
  cm: 96 / 2.54,
  mm: 96 / 25.4,
};

const UNITS = ["px", "rem", "em", "pt", "pc", "in", "cm", "mm"];

const COMMON_PRESETS = [
  { value: 16, unit: "px", labelKey: "css_unit_preset_body" },
  { value: 1, unit: "rem", labelKey: "css_unit_preset_one_rem" },
  { value: 0.75, unit: "rem", labelKey: "css_unit_preset_small" },
  { value: 1.5, unit: "rem", labelKey: "css_unit_preset_heading" },
  { value: 12, unit: "pt", labelKey: "css_unit_preset_print" },
  { value: 1, unit: "in", labelKey: "css_unit_preset_inch" },
];

const toPx = (value, unit, rootPx, parentPx) => {
  if (!Number.isFinite(value)) return NaN;
  if (unit === "rem") return value * rootPx;
  if (unit === "em") return value * parentPx;
  const factor = STATIC_TO_PX[unit];
  return factor === undefined ? NaN : value * factor;
};

const fromPx = (px, unit, rootPx, parentPx) => {
  if (!Number.isFinite(px)) return NaN;
  if (unit === "rem") return rootPx === 0 ? NaN : px / rootPx;
  if (unit === "em") return parentPx === 0 ? NaN : px / parentPx;
  const factor = STATIC_TO_PX[unit];
  return factor === undefined ? NaN : px / factor;
};

// Trim trailing zeros while keeping enough precision for everyday CSS values.
const formatNumber = (n) => {
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";
  const abs = Math.abs(n);
  const digits = abs >= 100 ? 2 : abs >= 1 ? 4 : 6;
  const fixed = n.toFixed(digits);
  return fixed.replace(/\.?0+$/, "");
};

const CssUnitConverter = () => {
  const { t } = useTranslation();
  const [rawValue, setRawValue] = useState("16");
  const [fromUnit, setFromUnit] = useState("px");
  const [rootSize, setRootSize] = useState("16");
  const [parentSize, setParentSize] = useState("16");

  const value = useMemo(() => {
    const n = parseFloat(rawValue);
    return Number.isFinite(n) ? n : NaN;
  }, [rawValue]);

  const rootPx = useMemo(() => {
    const n = parseFloat(rootSize);
    return Number.isFinite(n) && n > 0 ? n : 16;
  }, [rootSize]);

  const parentPx = useMemo(() => {
    const n = parseFloat(parentSize);
    return Number.isFinite(n) && n > 0 ? n : 16;
  }, [parentSize]);

  const valid = Number.isFinite(value);

  const results = useMemo(() => {
    if (!valid) return null;
    const px = toPx(value, fromUnit, rootPx, parentPx);
    return UNITS.reduce((acc, u) => {
      acc[u] = fromPx(px, u, rootPx, parentPx);
      return acc;
    }, {});
  }, [valid, value, fromUnit, rootPx, parentPx]);

  const applyPreset = (preset) => {
    setRawValue(String(preset.value));
    setFromUnit(preset.unit);
  };

  const copy = async (raw, unit) => {
    if (!Number.isFinite(raw)) {
      toast.error(t("css_unit_nothing_to_copy"));
      return;
    }
    const text = `${formatNumber(raw)}${unit}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const swap = (targetUnit) => {
    if (!results) return;
    const v = results[targetUnit];
    if (!Number.isFinite(v)) return;
    setRawValue(formatNumber(v));
    setFromUnit(targetUnit);
  };

  const reset = () => {
    setRawValue("16");
    setFromUnit("px");
    setRootSize("16");
    setParentSize("16");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("css_unit_title")}
            </h1>
            <p className="text-gray-600">{t("css_unit_intro")}</p>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("css_unit_value_label")}
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={rawValue}
                    onChange={(e) => setRawValue(e.target.value)}
                    placeholder="16"
                    className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                  {!valid && rawValue.trim() !== "" && (
                    <span className="text-xs text-red-600">
                      {t("css_unit_invalid_value")}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("css_unit_from_label")}
                  </label>
                  <select
                    value={fromUnit}
                    onChange={(e) => setFromUnit(e.target.value)}
                    className="w-full p-2 font-mono border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  >
                    {UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <StandardButton
                    onClick={reset}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("css_unit_reset")}
                  </StandardButton>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("css_unit_root_label")}
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={rootSize}
                    onChange={(e) => setRootSize(e.target.value)}
                    className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                  <span className="text-xs text-gray-500">
                    {t("css_unit_root_help")}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("css_unit_parent_label")}
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={parentSize}
                    onChange={(e) => setParentSize(e.target.value)}
                    className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                  <span className="text-xs text-gray-500">
                    {t("css_unit_parent_help")}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("css_unit_presets_label")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_PRESETS.map((p) => {
                    const active =
                      valid &&
                      fromUnit === p.unit &&
                      Math.abs(value - p.value) < 1e-9;
                    return (
                      <StandardButton
                        key={`${p.value}${p.unit}`}
                        onClick={() => applyPreset(p)}
                        type={active ? "primary" : "secondary"}
                        variant={active ? undefined : "outlined"}
                        size="small"
                      >
                        <span className="font-mono mr-1">
                          {p.value}
                          {p.unit}
                        </span>
                        {t(p.labelKey)}
                      </StandardButton>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium text-gray-700">
                {t("css_unit_results_label")}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200 rounded-md overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr className="text-gray-700">
                      <th className="text-left py-2 px-3 font-medium">
                        {t("css_unit_col_unit")}
                      </th>
                      <th className="text-left py-2 px-3 font-medium">
                        {t("css_unit_col_value")}
                      </th>
                      <th className="text-left py-2 px-3 font-medium">
                        {t("css_unit_col_actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {UNITS.map((u) => {
                      const v = results ? results[u] : NaN;
                      const isFrom = u === fromUnit;
                      return (
                        <tr
                          key={u}
                          className={`border-t border-gray-100 ${
                            isFrom ? "bg-indigo-50" : ""
                          }`}
                        >
                          <td className="py-2 px-3 font-mono text-gray-700">
                            {u}
                            {isFrom && (
                              <span className="ml-2 text-xs uppercase tracking-wide text-indigo-600">
                                {t("css_unit_source_tag")}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 font-mono text-indigo-700">
                            {formatNumber(v)}
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex flex-wrap gap-2">
                              <StandardButton
                                onClick={() => copy(v, u)}
                                type="secondary"
                                variant="outlined"
                                size="small"
                                disabled={!Number.isFinite(v)}
                              >
                                {t("copy_button")}
                              </StandardButton>
                              {!isFrom && (
                                <StandardButton
                                  onClick={() => swap(u)}
                                  type="secondary"
                                  variant="outlined"
                                  size="small"
                                  disabled={!Number.isFinite(v)}
                                >
                                  {t("css_unit_use_as_input")}
                                </StandardButton>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("css_unit_note_dpi")}</p>
              <p>{t("css_unit_note_em_vs_rem")}</p>
              <p>{t("css_unit_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CssUnitConverter;
